import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Wordmark } from "@/components/Wordmark";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage, NOTICE_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="grain relative grid min-h-screen bg-white lg:grid-cols-[1fr_1.1fr]">
      <div className="relative z-10 flex flex-col justify-between px-8 py-8 sm:px-16">
        <Wordmark size="lg" testId="auth-wordmark" />
        <div className="rise mx-auto w-full max-w-sm py-16">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-50">{title}</h1>
          <p className="mt-3 text-sm text-slate-400">{subtitle}</p>
          <div className="mt-10">{children}</div>
          <p className="mt-8 text-sm text-slate-500">{footer}</p>
        </div>
        <p className="text-xs text-slate-600">© Viewlux Windows and Doors</p>
      </div>
      <div className="relative hidden overflow-hidden bg-[#0071E3] lg:block">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_25%,rgba(255,255,255,0.18),transparent_55%)]" />
        <div className="absolute bottom-16 left-16 right-16">
          <p className="text-[13px] font-semibold uppercase tracking-[0.3em] text-white/90">Viewlux</p>
          <p className="mt-6 font-display text-5xl font-bold leading-tight text-white">
            Every window. Every door.<br /><span className="text-white/90">Priced your way.</span>
          </p>
          <p className="mt-5 max-w-md text-[17px] leading-relaxed text-white">Upload a schedule, check the table, send a branded estimate. Your costs and markup stay private.</p>
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, ...props }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</Label>
      <Input id={id} {...props} className="h-11 border-slate-800 bg-slate-900/70 text-slate-100 focus-visible:ring-amber-500" />
    </div>
  );
}

function useAuthSubmit(path) {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(() => sessionStorage.getItem(NOTICE_KEY) || "");
  const [busy, setBusy] = useState(false);
  const submit = async (payload) => {
    sessionStorage.removeItem(NOTICE_KEY);
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(path, payload);
      signIn(data);
      navigate(data.user?.role === "superadmin" ? "/admin" : "/dashboard");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { error, busy, submit };
}

const SubmitButton = ({ busy, children, testId }) => (
  <button type="submit" disabled={busy} data-testid={testId}
    className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-amber-500 text-sm font-semibold text-white transition-colors hover:bg-amber-600 disabled:opacity-60">
    {busy && <Loader2 className="h-4 w-4 animate-spin" />} {children}
  </button>
);

const ErrorText = ({ error, testId }) =>
  error ? <p data-testid={testId} className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</p> : null;

export function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const { error, busy, submit } = useAuthSubmit("/auth/login");
  return (
    <AuthLayout title="Welcome back" subtitle="Log in to your Viewlux workspace."
      footer={<>Need an account? Ask the Viewlux owner to add you on the Team page.</>}>
      <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); submit(form); }}>
        <Field id="email" label="Email" type="email" required autoComplete="email" data-testid="login-email-input"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <Field id="password" label="Password" type="password" required autoComplete="current-password" data-testid="login-password-input"
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <ErrorText error={error} testId="login-error" />
        <SubmitButton busy={busy} testId="login-submit-button">Log in</SubmitButton>
      </form>
    </AuthLayout>
  );
}
